import React, { useState, useRef, useCallback } from 'react';
import { Timecode, DrawingTool, MentionUser } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { useMentionInput } from '../hooks/useMentionInput';
import { useVoiceDictation } from '../hooks/useVoiceDictation';
import MentionAutocomplete from './MentionAutocomplete';
import {
  PencilIcon,
  EraserIcon,
  CircleIcon,
  SquareIcon,
  LineIcon,
  UndoIcon,
  TrashIcon,
  RecordIcon,
  MicrophoneIcon,
  AttachFileIcon,
} from './playerIcons';

interface CommentInputBarProps {
  onAddComment: (
    text: string,
    recording?: { blob: Blob; duration: number },
    attachment?: File
  ) => void | Promise<void>;
  currentTimecode: Timecode;
  isDrawingEnabled: boolean;
  onToggleDrawing: (enabled: boolean) => void;
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  recordingTime: number;
  drawingTool: DrawingTool;
  setDrawingTool: (tool: DrawingTool) => void;
  drawingColor: string;
  setDrawingColor: (color: string) => void;
  onUndo: () => void;
  onClear: () => void;
  users?: MentionUser[];
}

const ToolButton: React.FC<{
  title: string;
  toolName: DrawingTool;
  activeTool: DrawingTool;
  setTool: (tool: DrawingTool) => void;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ title, toolName, activeTool, setTool, disabled, children }) => (
  <button
    type="button"
    onClick={() => setTool(toolName)}
    disabled={disabled}
    className={`p-1.5 rounded-lg transition-all ${
      activeTool === toolName
        ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
        : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
    } disabled:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed`}
    title={title}
  >
    {children}
  </button>
);

const ColorButton: React.FC<{
  color: string;
  activeColor: string;
  setColor: (color: string) => void;
  disabled: boolean;
}> = ({ color, activeColor, setColor, disabled }) => (
  <button
    type="button"
    onClick={() => setColor(color)}
    disabled={disabled}
    className={`w-5 h-5 rounded-full transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 ${
      activeColor === color
        ? 'ring-2 ring-offset-2 ring-offset-gray-800 ring-white scale-110'
        : 'opacity-70 hover:opacity-100'
    }`}
    style={{ backgroundColor: color }}
    title={color}
  />
);

const MAX_ATTACHMENT_SIZE_MB = 100;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const DRAWING_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ffffff'];

const CommentInputBar: React.FC<CommentInputBarProps> = ({
  onAddComment,
  currentTimecode,
  isDrawingEnabled,
  onToggleDrawing,
  isRecording,
  onStartRecording,
  onStopRecording,
  recordingTime,
  drawingTool,
  setDrawingTool,
  drawingColor,
  setDrawingColor,
  onUndo,
  onClear,
  users = [],
}) => {
  const { t } = useTranslation();
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const textareaContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    text: commentText,
    setText: setCommentText,
    cursorPosition,
    setCursorPosition,
    handleTextChange,
    handleKeyDown: handleMentionKeyDown,
    insertMention,
    textareaRef,
  } = useMentionInput({
    users,
    onMentionSelect: () => setShowMentionAutocomplete(false),
  });

  const handleDictationComplete = useCallback(
    (text: string) => {
      setCommentText((prev) => {
        const separator = prev.trim() ? ' ' : '';
        return prev + separator + text;
      });
    },
    [setCommentText]
  );

  const { isRecording: isDictating, isTranscribing, startDictation, stopDictation } =
    useVoiceDictation({ onTranscriptionComplete: handleDictationComplete });

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;
    if (!(commentText.trim() || isDrawingEnabled || selectedFile)) return;

    const text = commentText.trim();
    const file = selectedFile || undefined;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onAddComment(text, undefined, file);
      setCommentText('');
      setSelectedFile(null);
      setShowMentionAutocomplete(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Parent clears drawing after successful save — do not toggle off before await
    } catch (err) {
      console.error('Failed to post comment:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setFileError(t('player.fileTooLarge', { max: MAX_ATTACHMENT_SIZE_MB }));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setFileError(null);
      setSelectedFile(file);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    handleMentionKeyDown(e);
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleTextChange(e);
    const newText = e.target.value;
    const pos = e.target.selectionStart;
    let start = pos - 1;
    while (start >= 0 && newText[start] !== '@' && newText[start] !== ' ' && newText[start] !== '\n') {
      start--;
    }
    setShowMentionAutocomplete(start >= 0 && newText[start] === '@');
  };

  const tc = `${String(currentTimecode.minutes).padStart(2, '0')}:${String(currentTimecode.seconds).padStart(2, '0')}:${String(currentTimecode.frames).padStart(2, '0')}`;

  return (
    <div className="bg-gradient-to-t from-gray-900 to-gray-800/95 backdrop-blur-sm border-t border-gray-700/50 w-full flex-shrink-0 overflow-x-hidden relative z-30">
      <div className="max-w-3xl mx-auto px-3 py-3">
        <div className="bg-gray-900/80 rounded-xl border border-gray-700/50 shadow-lg overflow-hidden">
          <div className="flex items-center gap-1 px-3 py-2 bg-gray-800/50 border-b border-gray-700/30">
            <button
              type="button"
              onClick={() => onToggleDrawing(!isDrawingEnabled)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex-shrink-0 whitespace-nowrap ${
                isDrawingEnabled
                  ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
                  : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
              }`}
              title={isDrawingEnabled ? t('player.drawingModeOn') : t('player.drawingMode')}
            >
              {t('player.drawingMode')}
            </button>

            <div className="h-4 w-px bg-gray-600/50 mx-1" />

            <div className="flex items-center gap-0.5">
              <ToolButton title="Pencil" toolName="pencil" activeTool={drawingTool} setTool={setDrawingTool} disabled={!isDrawingEnabled}>
                <PencilIcon className="w-4 h-4" />
              </ToolButton>
              <ToolButton title="Line" toolName="line" activeTool={drawingTool} setTool={setDrawingTool} disabled={!isDrawingEnabled}>
                <LineIcon className="w-4 h-4" />
              </ToolButton>
              <ToolButton title="Square" toolName="square" activeTool={drawingTool} setTool={setDrawingTool} disabled={!isDrawingEnabled}>
                <SquareIcon className="w-4 h-4" />
              </ToolButton>
              <ToolButton title="Circle" toolName="circle" activeTool={drawingTool} setTool={setDrawingTool} disabled={!isDrawingEnabled}>
                <CircleIcon className="w-4 h-4" />
              </ToolButton>
            </div>

            <div className="h-4 w-px bg-gray-600/50 mx-1" />

            <div className="flex items-center gap-1">
              {DRAWING_COLORS.map((color) => (
                <ColorButton
                  key={color}
                  color={color}
                  activeColor={drawingColor}
                  setColor={setDrawingColor}
                  disabled={!isDrawingEnabled}
                />
              ))}
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={onUndo}
                disabled={!isDrawingEnabled}
                className="p-1.5 rounded-lg transition-colors text-gray-400 hover:bg-gray-700 hover:text-gray-200 disabled:text-gray-600 disabled:cursor-not-allowed"
                title={t('player.undo')}
              >
                <UndoIcon className="w-4 h-4" />
              </button>
              <ToolButton title="Eraser" toolName="eraser" activeTool={drawingTool} setTool={setDrawingTool} disabled={!isDrawingEnabled}>
                <EraserIcon className="w-4 h-4" />
              </ToolButton>
              <button
                type="button"
                onClick={onClear}
                disabled={!isDrawingEnabled}
                className="p-1.5 rounded-lg transition-colors text-gray-400 hover:bg-red-500/20 hover:text-red-400 disabled:text-gray-600 disabled:cursor-not-allowed"
                title={t('player.clear')}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-3">
            {submitError && (
              <div className="mb-2 text-xs text-red-400 bg-red-900/30 border border-red-800/50 rounded-lg px-3 py-2">
                {submitError}
              </div>
            )}
            <div className="flex items-center gap-2">
              <div ref={textareaContainerRef} className="relative flex-grow min-w-0">
                <textarea
                  ref={textareaRef}
                  value={commentText}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  onSelect={(e) => setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)}
                  placeholder={
                    isDictating
                      ? t('player.dictate')
                      : `${t('player.commentPlaceholder')} (${tc})`
                  }
                  disabled={isSubmitting}
                  className={`w-full bg-gray-800 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500/50 focus:outline-none transition-all resize-none min-h-[40px] border ${
                    isDictating ? 'border-indigo-500 bg-indigo-900/20' : 'border-transparent hover:border-gray-600'
                  } disabled:opacity-60`}
                  rows={1}
                />
                {showMentionAutocomplete && users.length > 0 && (
                  <MentionAutocomplete
                    text={commentText}
                    cursorPosition={cursorPosition}
                    users={users}
                    onSelect={(id, name) => {
                      insertMention(id, name);
                      setShowMentionAutocomplete(false);
                    }}
                    onClose={() => setShowMentionAutocomplete(false)}
                    containerRef={textareaContainerRef}
                  />
                )}
                {fileError && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-400 bg-red-900/40 rounded-full px-2 py-0.5">
                    {fileError}
                  </div>
                )}
                {selectedFile && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-gray-400 bg-gray-700/80 rounded-full px-2 py-0.5">
                    <span className="truncate max-w-[80px]" title={selectedFile.name}>
                      {selectedFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="text-red-400 hover:text-red-300 font-bold leading-none ml-1"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" id="comment-attachment" />
                <label
                  htmlFor="comment-attachment"
                  className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 cursor-pointer transition-colors flex items-center justify-center"
                  title={t('player.attachFile')}
                >
                  <AttachFileIcon className="w-5 h-5" />
                </label>

                {isDictating || isTranscribing ? (
                  <button
                    type="button"
                    onClick={stopDictation}
                    className="p-2 rounded-lg bg-red-500 text-white shadow-lg shadow-red-500/30"
                    title={t('player.stopDictation')}
                  >
                    <div className="w-4 h-4 bg-white rounded-sm animate-pulse" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startDictation}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-indigo-600 hover:text-white transition-all"
                    title={t('player.dictate')}
                  >
                    <MicrophoneIcon className="w-5 h-5" />
                  </button>
                )}

                {isRecording ? (
                  <button
                    type="button"
                    onClick={onStopRecording}
                    className="p-2 rounded-lg bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30 min-w-[52px] flex items-center justify-center gap-1"
                    title={t('player.stopRecording')}
                  >
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span className="font-mono text-xs font-bold">{(recordingTime / 1000).toFixed(1)}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onStartRecording}
                    className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 transition-colors"
                    title={t('player.record')}
                  >
                    <RecordIcon className="w-5 h-5 text-red-700" />
                  </button>
                )}

                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-all text-sm shadow-lg shadow-indigo-500/20 min-w-[5.5rem]"
                  disabled={isSubmitting || (!commentText.trim() && !isDrawingEnabled && !selectedFile)}
                >
                  {isSubmitting ? '…' : t('player.send')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommentInputBar;
